import { type ReactElement } from 'react'
import type { GeoPoint, GeoPointsGroup, StopsSequenceGroup } from 'screens/TripDetails/TripDetails.types'
import type { StopsLocationGroup } from 'screens/TripDashboard/TripDashboard.types'
import type { VehicleData } from 'screens/VehicleManager/VehicleManager.types'

export interface KNMapLibreProps {
  markers?: MapMarker[]
  geoPoints?: GeoPoint[]
  groupedGeoPoints?: GeoPointsGroup[]
  shortestRoutes?: GeoPoint[][]
  focusGeoPoint?: GeoPoint
  withHeading?: boolean
  withoutLayersControl?: boolean
  onMarkerClick?: (marker: MapMarker) => void
  withSelectors?: boolean
  selectionSource?: string
  selectionMode?: MapSelectionMode
  selections?: MapSelection[]
  onSelectionChange?: (selection: MapSelection) => void
  customAttribution?: string
}

export interface KNMapLibreMarkerProps {
  marker: MapMarker
  onMarkerClick?: (marker: MapMarker) => void
}

export interface KNMapLibreTooltipProps {
  markers: MapMarker[]
}

export interface KNMapLibreTooltipWrapperProps {
  content?: ReactElement
  icon?: ReactElement
}

export interface KNMapLibreSourceSelectorProps {
  source: string
  mode?: MapSelectionMode
  selections: MapSelection[]
  onChange: (selection: MapSelection) => void
}

export interface KNMapLibreCircleSelectorProps {
  mode?: MapSelectionMode
  selections: MapSelection[]
  onChange: (selection: MapSelection) => void
}

export enum MapMarkerState {
  Default,
  Active,
  Muted,
}

export type MapMarker = BasicMapMarker | SequenceGroupMapMarker | LocationGroupMapMarker

export interface BasicMapMarker {
  id: string
  latitude: number
  longitude: number
  type?: string
  subType?: string
  color?: string
  label?: string
  heading?: number
  geofenceRadius?: number // radius in meters
  geofencePolygon?: GeoPoint[]
  state?: MapMarkerState
  tooltip?: ReactElement
  compactTooltip?: ReactElement
  grouped?: boolean
  groupable?: boolean
}

export interface SequenceGroupMapMarker extends BasicMapMarker {
  sequenceGroup: StopsSequenceGroup
}

export interface LocationGroupMapMarker extends BasicMapMarker {
  locationGroup: StopsLocationGroup
}

export interface VehicleMapMarker extends BasicMapMarker {
  vehicle: VehicleData
  tripCids: string[]
}

export const hasSequenceGroup = (marker: MapMarker): marker is SequenceGroupMapMarker =>
  'sequenceGroup' in marker

export const hasLocationGroup = (marker: MapMarker): marker is LocationGroupMapMarker =>
  'locationGroup' in marker

export const hasVehicleData = (marker: MapMarker): marker is VehicleMapMarker =>
  'vehicle' in marker

export interface MapTooltip {
  id: string
  content: ReactElement
  priority?: number
}

export enum MapLayerType {
  TRACKED_ROUTES = 'TRACKED_ROUTES',
  SHORTEST_ROUTES = 'SHORTEST_ROUTES',
  PARKING_LOCATIONS = 'PARKING_LOCATIONS',
}

export type MapLayers = {
  [key in MapLayerType]: boolean
}

export interface MapSelection {
  mode: MapSelectionMode
  color?: string
}

export enum MapSelectionMode {
  SOURCE = 'SOURCE',
  CIRCLE = 'CIRCLE',
}

export interface MapSourceSelection extends MapSelection {
  source: string
  code: string
}

export interface MapCircleSelection extends MapSelection {
  center: [number, number]
  radius: number
}

export interface MapSourceGroup {
  id: number
  layerId: string
  code: string
  name: string
  country_code: string
  cluster: string
  latitude: number
  longitude: number
  restricted: boolean
}
